import { Component, computed, inject, input } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { InlineLoadingService } from './inline-loading.service';

/**
 * Inline loading component — same pattern as LoadingOverlayComponent.
 * Place it in the template inside a `position: relative` container and
 * call InlineLoadingService.show(key) / hide(key) from TypeScript.
 *
 * Usage (template):
 *   <div class="relative">
 *     <app-inline-loading [loadingKey]="'my-key'" />
 *     <!-- content shown when not loading -->
 *   </div>
 *
 * Usage (TypeScript):
 *   inlineLoadingService.show('my-key', 'i18n.message_key');
 *   inlineLoadingService.hide('my-key');
 */
@Component({
  selector: 'app-inline-loading',
  standalone: true,
  imports: [MatProgressSpinnerModule, TranslateModule],
  templateUrl: './inline-loading.component.html',
  styleUrls: ['./inline-loading.component.scss'],
})
export class InlineLoadingComponent {
  private readonly inlineLoadingService = inject(InlineLoadingService);

  /** Key to scope this instance to a specific loading state. */
  readonly loadingKey = input<string>('default');

  /** Spinner diameter in pixels. Default: 48. */
  readonly diameter = input<number>(48);

  private readonly _state = computed(() => this.inlineLoadingService.getState(this.loadingKey())());

  readonly isLoading = computed(() => this._state().count > 0);

  /** i18n key to translate inside the template — translations always work. */
  readonly activeMessageKey = computed(() => this._state().messageKey);
}
